import { useEffect, useRef } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useGetMe } from '@/hooks/useGetMe';
import { Button, MoneyAccentField } from '@scramble/ui';
import { useGetWithdrawInfo } from '@/query/queries/cash/useGetWithdrawInfo';
import { ReviewWithdrawModal } from '@/modals/ReviewWithdrawModal';
import { BankAccountAddWarningModal } from '@/modals/BankAccountAddWarningModal';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { WithdrawalDestination } from '../WithdrawalDestination';
import { ImportantNotes } from '../ImportantNotes';
import { AmountPresets } from '../AmountPresets';
import { prettyMoney } from '@scramble/utils';
import { VALIDATION_SCHEMA } from './schema';
import s from './index.module.scss';

export const WithdrawForm = ({ children }) => {
  const { canWithdraw } = useGetMe();
  const { openSingleModal  } = useModal();
  const { data } = useGetWithdrawInfo();
  const { t } = useTranslation();

  const balance = data?.cash || 0;
  const accounts = data?.external_accounts || [];
  const hasAccounts = accounts.length > 0;
  const hasOpenedWarning = useRef(false);

  useEffect(() => {
    if (data && !hasAccounts && !hasOpenedWarning.current) {
      openSingleModal(MODAL_IDS.bank_account_add_warning_modal);
      hasOpenedWarning.current = true;
    }
  }, [data, hasAccounts]);

  const onSubmit = () => {
    openSingleModal(MODAL_IDS.review_withdraw_modal);
  }

  return (
    <div className={s.wrapper}>
      <Formik
        onSubmit={onSubmit}
        enableReinitialize
        validateOnMount
        validationSchema={VALIDATION_SCHEMA(t, balance, hasAccounts)}
        initialValues={{
          amount: '',
          destination: (accounts && accounts[0]) || null,
        }}
      >
        {({ values, errors, dirty, handleSubmit, setFieldValue }) => (
          <>
            <form className={s.form} onSubmit={handleSubmit}>
              <MoneyAccentField
                isFloat
                label={t('withdraw:form.amount')}
                name="amount"
                value={values.amount}
                placeholder="0.00"
                onChange={amount => {
                  setFieldValue('amount', amount);
                }}
                onClear={() => {
                  setFieldValue('amount', '');
                }}
                error={
                  dirty && typeof errors.amount === 'string'
                    ? errors.amount
                    : undefined
                }
                description={t('withdraw:form.amount_available', {
                  amount: prettyMoney(data?.cash),
                })}
              />

              <AmountPresets
                amount={values.amount}
                availableAmount={balance}
                onClick={amount => setFieldValue('amount', amount)}
                className={s.presets}
              />

              <WithdrawalDestination
                selected={values.destination}
                className={s.destination}
                accounts={accounts}
                onChange={account => setFieldValue('destination', account)}
              />

              <Button
                type="submit"
                label={t('withdraw:form.review')}
                theme="neutral"
                appearance="filled"
                size="lg"
                disabled={!canWithdraw || !!Object.keys(errors).length}
                className={s.btn_submit}
              />

              <ReviewWithdrawModal
                amount={values.amount}
                destination={values.destination}
              />
              {children}
            </form>

            <ImportantNotes destination={values.destination} />
          </>
        )}
      </Formik>
      <BankAccountAddWarningModal />
    </div>
  );
};
